import React from 'react';
import { config, useSpring } from '@react-spring/web';
import { useInterval } from '@mentimeter/react-hooks';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import type { SlideStateOpen } from '../../slide-state';
import { BOTTOM_PADDING_VALUE } from '../responses-view/FlowingGrid';

const SCROLL_DELAY = 2000;
const HEIGHT_TO_SCROLL = 300;

export const useAnimatedScroll = () => {
  const { useConfig, useSlideState } = usePresentationContext();
  const [slideState, setSlideState] = useSlideState<SlideStateOpen>();

  const shouldAnimateScroll =
    useConfig().enableAnimations && !slideState.shouldPauseScroll;
  const animatedScrollEvent = React.useRef(false);

  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const handleScroll = React.useCallback(() => {
    if (!animatedScrollEvent.current) {
      setSlideState({ shouldPauseScroll: true });
    }
    animatedScrollEvent.current = false;
  }, [setSlideState]);

  const [_, setY] = useSpring(() => ({
    config: config.slow,
    y: 0,
  }));

  useInterval(
    () => {
      if (!contentRef.current || !containerRef.current) return;

      const hasMoreHeight =
        contentRef.current.scrollHeight -
          (containerRef.current.scrollTop +
            containerRef.current.offsetHeight) >=
        BOTTOM_PADDING_VALUE;

      if (!hasMoreHeight) return;
      return setY({
        from: {
          y: containerRef.current?.scrollTop,
        },
        to: {
          y: containerRef.current?.scrollTop + HEIGHT_TO_SCROLL,
        },
        onChange: ({ value }) => {
          animatedScrollEvent.current = true;
          if (containerRef.current) {
            containerRef.current.scrollTop = value.y;
          }
        },
      });
    },
    shouldAnimateScroll ? SCROLL_DELAY : null,
  );

  return {
    contentRef,
    containerRef,
    // N.B. The pauseScroll check is set here, to prevent unnecessary returns of the onScroll event
    handleScroll: slideState.shouldPauseScroll ? undefined : handleScroll,
  };
};
